import React from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const CARD_BORDER = "#e3e9f2";

/* ======================================================
   METRIC CONFIG
====================================================== */
const METRIC_CONFIG = [
  { key: "active_members", label: "Active Members", color: BRAND_GREEN },

  { key: "new_members", label: "New Members", color: "#1B7F79" },

  {
    key: "member_revenue",
    label: "Total Revenue from Members",
    color: "#2E7D32",
    format: (v) =>
      `$${Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(v || 0)}`,
  },

  { key: "inquiries", label: "Inquiries", color: "#E65100" },

  { key: "leads", label: "Leads - In Conversation With", color: "#6A5ACD" },

  {
    key: "qualified_opportunities",
    label: "Qualified Opportunities",
    color: "#6D28D9",
  },

  {
    key: "rfis_responded_to",
    label: "RFIs Responded To",
    color: "#0F766E",
  },

  {
    key: "prospect_closed_won",
    label: "Prospects (Closed Won)",
    color: "#047857",
  },

  {
    key: "prospect_closed_lost",
    label: "Prospects (Closed Lost)",
    color: "#B91C1C",
  },

  {
    key: "prospects_added",
    label: "Prospects Added",
    color: BRAND_GREEN,
  },

  { key: "website_visitors", label: "Website Visitors", color: "#17A2B8" },

  {
    key: "bre_companies",
    label: "BR&E Companies Tracked",
    color: BRAND_BLUE,
  },

  { key: "jobs", label: "Jobs Created", color: "#C47A2C" },

  {
    key: "investment",
    label: "Investment",
    color: "#2E7D32",
    format: (v) =>
      `$${Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(v || 0)}`,
  },

  { key: "projects_won", label: "New Projects", color: "#8B3A62" },
];

const TICK_STYLE = {
  fill: BRAND_BLUE,
  fontSize: 12,
  fontWeight: 600,
};

/* ======================================================
   CUSTOM TOOLTIP
====================================================== */
function CustomTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;

  const [y, m] = label.split("-");
  const formattedDate = new Date(y, m - 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const entry = payload[0];
  const value = entry.value;
  const metricLabel = entry.name || entry.dataKey;

  return (
    <Box
      sx={{
        background: BRAND_BLUE,
        border: `2px solid ${BRAND_BLUE}`,
        borderRadius: 2,
        px: 2,
        py: 1.5,
        boxShadow: "0 10px 24px rgba(0, 51, 108, 0.28)",
        textAlign: "center",
      }}
    >
      <Typography sx={{ fontWeight: 700, color: "#ffffff", mb: 0.5 }}>
        {formattedDate}
      </Typography>

      <Typography sx={{ color: "#ffffff", fontWeight: 600 }}>
        {metricLabel}: {formatter ? formatter(value) : value}
      </Typography>
    </Box>
  );
}

/* ======================================================
   MAIN COMPONENT
====================================================== */
export default function MetricCharts({ rows = [], selectedMetrics = [], pdfMode = false }) {
  if (!rows.length || !selectedMetrics.length) return null;

  const visibleMetrics = METRIC_CONFIG.filter((m) =>
    selectedMetrics.includes(m.key),
  );

  if (!visibleMetrics.length) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: pdfMode
          ? "repeat(2, 1fr)"
          : { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, minmax(0, 1fr))" },
        gap: 2,
        minWidth: 0,
      }}
    >
      {visibleMetrics.map((m) => (
        <Chart
          key={m.key}
          title={m.label}
          rows={rows}
          dataKey={m.key}
          color={m.color}
          formatter={m.format}
          pdfMode={pdfMode}
        />
      ))}
    </Box>
  );
}

/* ======================================================
   CHART COMPONENT
====================================================== */
function Chart({ title, rows, dataKey, color, formatter, pdfMode = false }) {
  return (
    <Card
      data-pdf-chart
      sx={{
        borderRadius: 2,
        border: `1px solid ${CARD_BORDER}`,
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 800,
              color: BRAND_BLUE,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              ml: "auto",
              px: 1,
              py: 0.45,
              borderRadius: 1.25,
              backgroundColor: `${color}18`,
              color,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Monthly
          </Box>
        </Box>

        <Box sx={{ width: "100%", minWidth: 0, height: pdfMode ? 340 : 170, minHeight: pdfMode ? 340 : 170 }}>
          <ResponsiveContainer
            width="100%"
            height="100%"
            minWidth={0}
            initialDimension={{ width: 360, height: pdfMode ? 340 : 170 }}
          >
            <BarChart
              data={rows}
              margin={{ top: 8, right: 6, left: -12, bottom: 0 }}
            >
              <CartesianGrid
                vertical={false}
                stroke="#e8edf4"
                strokeOpacity={1}
              />

            <XAxis
              dataKey="period"
              axisLine={false}
              tickLine={false}
              tick={{
                ...TICK_STYLE,
                fill: "#8796b4",
                fontSize: 11,
                fontWeight: 500,
              }}
              tickFormatter={(v) => {
                const [y, m] = v.split("-");
                return new Date(y, m - 1).toLocaleDateString("en-US", {
                  month: "short",
                });
              }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                ...TICK_STYLE,
                fill: "#8796b4",
                fontSize: 11,
                fontWeight: 500,
              }}
              tickFormatter={formatter}
            />

            <Tooltip content={<CustomTooltip formatter={formatter} />} />

              <Bar
                dataKey={dataKey}
                fill={color}
                name={title}
                radius={[4, 4, 0, 0]}
                barSize={14}
                isAnimationActive={!pdfMode}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}
